import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { compact } from 'lodash';
import PropTypes from 'prop-types';

export const REASON_LABELS = {
  GLOBAL_COMPOUNDING_DISABLED: 'Compounding disabled',
  PARTNER_COMPOUNDING_DISABLED: 'Partner compounding disabled',
  NOT_COMPOUNDING_DRUG: 'Not a compounding drug',
  PRODUCT_MAPPING_MISSING: 'Product mapping missing',
  STATE_NOT_ELIGIBLE: 'State not eligible',
  ALREADY_PROCESSED: 'Order already processed',
  LINE_ITEM_NOT_FOUND: 'Not found',
  ORDER_NOT_FOUND: 'Not found',
};

export const NA = 'N/A';

const labelForReason = (reason) => REASON_LABELS[reason] || reason;

export const formatReasons = (reasons) => compact((reasons || []).map(labelForReason)).join(', ');

const resultRowShape = PropTypes.shape({
  lineItemId: PropTypes.string.isRequired,
  orderId: PropTypes.string,
  patientName: PropTypes.string,
  drugName: PropTypes.string,
  rxNumber: PropTypes.string,
  shipByDate: PropTypes.string,
  facilityLabel: PropTypes.string,
  sourceLabel: PropTypes.string,
  isEligible: PropTypes.bool,
  ineligibilityReasons: PropTypes.arrayOf(PropTypes.string),
});

const OrderIdCell = ({ orderId }) => orderId || NA;

OrderIdCell.propTypes = { orderId: PropTypes.string };
OrderIdCell.defaultProps = { orderId: undefined };

const RESULT_COLUMNS = ['Order Id', 'Drug', 'Rx #', 'Partner', 'Status', 'Reason'];

// Matches the app's standard status chip (CentralFillQueueStatusChip): a plain
// MUI Chip using the `color` prop rather than custom hex pills.
const EligibilityStatusChip = ({ isEligible }) => (
  <Chip
    size='small'
    variant='outlined'
    label={isEligible ? 'Eligible' : 'Not eligible'}
    color={isEligible ? 'success' : 'error'}
    sx={{ fontWeight: 600 }}
  />
);

EligibilityStatusChip.propTypes = { isEligible: PropTypes.bool };
EligibilityStatusChip.defaultProps = { isEligible: false };

// Single results table: one row per order with an explicit Status column, instead
// of two separate eligible/ineligible tables. Rows are expected pre-sorted by the
// caller (ineligible first). Reason is only shown for ineligible rows.
export const EligibilityResultsTable = ({ rows, maxHeight }) => (
  <TableContainer
    sx={{
      maxHeight,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      '&::-webkit-scrollbar': { width: 10, height: 10 },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#cfd4dc',
        borderRadius: 999,
        border: '3px solid #fff',
      },
    }}
  >
    <Table
      size='small'
      stickyHeader
      aria-label='Eligibility results table'
      sx={{
        '& td, & th': { py: 0.75 },
        '& tbody td': { borderColor: '#f0f2f6', fontVariantNumeric: 'tabular-nums' },
        '& tbody tr:last-child td': { border: 0 },
      }}
    >
      <TableHead>
        <TableRow
          sx={{
            '& th': {
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              bgcolor: '#f8f9fb',
            },
          }}
        >
          {RESULT_COLUMNS.map((column) => (
            <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const reasonText = formatReasons(row.ineligibilityReasons) || 'Not eligible';
          return (
            <TableRow key={row.lineItemId} hover>
              <TableCell>
                <OrderIdCell orderId={row.orderId} />
              </TableCell>
              <TableCell>{row.drugName || NA}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.rxNumber || NA}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.partnerName || NA}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <EligibilityStatusChip isEligible={row.isEligible} />
              </TableCell>
              <TableCell sx={{ color: row.isEligible ? 'text.disabled' : 'error.main' }}>
                {row.isEligible ? '—' : reasonText}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

EligibilityResultsTable.propTypes = {
  rows: PropTypes.arrayOf(resultRowShape).isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
EligibilityResultsTable.defaultProps = { maxHeight: 360 };
